<template>
  <div class="admin-login-wrapper">
    <div class="admin-login-card" :class="{ 'shake': loginError }">
      <div class="admin-login-header">
        <div class="admin-logo">
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
            <rect width="40" height="40" rx="10" fill="url(#adminGrad)" />
            <path d="M12 28V16l8-6 8 6v12H24v-8h-8v8z" fill="#fff" />
            <defs>
              <linearGradient id="adminGrad" x1="0" y1="0" x2="40" y2="40">
                <stop stop-color="#6366f1" />
                <stop offset="1" stop-color="#8b5cf6" />
              </linearGradient>
            </defs>
          </svg>
        </div>
        <h1>Admin Panel</h1>
        <p class="admin-login-subtitle">Gstocky Platform Management</p>
      </div>

      <form @submit.prevent="handleLogin" class="admin-login-form">
        <div class="admin-input-group">
          <label for="admin-email">Email</label>
          <div class="admin-input-wrapper">
            <svg class="admin-input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
            <input
              id="admin-email"
              v-model="email"
              type="email"
              placeholder="admin@gstocky.com"
              required
              autocomplete="email"
            />
          </div>
        </div>

        <div class="admin-input-group">
          <label for="admin-password">Password</label>
          <div class="admin-input-wrapper">
            <svg class="admin-input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            <input
              id="admin-password"
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              placeholder="••••••••"
              required
              autocomplete="current-password"
            />
            <button type="button" class="admin-password-toggle" @click="showPassword = !showPassword">
              <svg v-if="!showPassword" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
              <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                <line x1="1" y1="1" x2="23" y2="23"/>
              </svg>
            </button>
          </div>
        </div>

        <div v-if="errorMessage" class="admin-error-msg">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
          {{ errorMessage }}
        </div>

        <button type="submit" class="admin-login-btn" :disabled="loading">
          <span v-if="!loading">Sign In</span>
          <span v-else class="admin-spinner"></span>
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'AdminLogin',
  data() {
    return {
      email: '',
      password: '',
      showPassword: false,
      loading: false,
      errorMessage: '',
      loginError: false
    }
  },
  created() {
    // If already logged in as admin, redirect
    if (localStorage.getItem('admin_token')) {
      this.$router.push('/admin/dashboard')
    }
  },
  methods: {
    async handleLogin() {
      this.loading = true
      this.errorMessage = ''
      this.loginError = false

      try {
        const response = await axios.post(`${process.env.VUE_APP_API}/admin/login`, {
          email: this.email,
          password: this.password
        })

        if (response.data.success) {
          localStorage.setItem('admin_token', response.data.token)
          localStorage.setItem('admin_user', JSON.stringify(response.data.user))
          this.$router.push('/admin/dashboard')
        }
      } catch (error) {
        this.loginError = true
        this.errorMessage = error.response?.data?.message || 'Login failed. Please try again.'
        setTimeout(() => { this.loginError = false }, 600)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.admin-login-wrapper {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f4f7fb;
  position: relative;
  overflow: hidden;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.admin-login-card {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 420px;
  padding: 40px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  box-shadow: 0 24px 50px rgba(15, 23, 42, 0.12);
  transition: transform 0.3s ease;
}

.admin-login-card.shake {
  animation: shakeCard 0.5s ease;
}

@keyframes shakeCard {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-10px); }
  40% { transform: translateX(10px); }
  60% { transform: translateX(-6px); }
  80% { transform: translateX(6px); }
}

.admin-login-header {
  text-align: center;
  margin-bottom: 32px;
}

.admin-logo {
  display: inline-flex;
  margin-bottom: 16px;
}

.admin-login-header h1 {
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 6px 0;
  letter-spacing: -0.5px;
}

.admin-login-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.admin-login-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.admin-input-group label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  margin-bottom: 8px;
}

.admin-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.admin-input-icon {
  position: absolute;
  left: 14px;
  color: #475569;
  pointer-events: none;
}

.admin-input-wrapper input {
  width: 100%;
  padding: 12px 14px 12px 44px;
  background: #ffffff;
  border: 1px solid #dbe3ef;
  border-radius: 12px;
  color: #111827;
  font-size: 14px;
  font-family: inherit;
  transition: all 0.2s ease;
  outline: none;
}

.admin-input-wrapper input:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
  background: #ffffff;
}

.admin-input-wrapper input::placeholder {
  color: #475569;
}

.admin-password-toggle {
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  color: #475569;
  cursor: pointer;
  padding: 4px;
  display: flex;
  transition: color 0.2s;
}

.admin-password-toggle:hover {
  color: #94a3b8;
}

.admin-error-msg {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 10px;
  color: #fca5a5;
  font-size: 13px;
}

.admin-login-btn {
  width: 100%;
  padding: 13px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  border: none;
  border-radius: 12px;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  margin-top: 4px;
}

.admin-login-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 25px rgba(99, 102, 241, 0.35);
}

.admin-login-btn:active:not(:disabled) {
  transform: translateY(0);
}

.admin-login-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.admin-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 480px) {
  .admin-login-card {
    margin: 16px;
    padding: 28px 24px;
  }
}
</style>
